import { Link } from 'react-router-dom';
import { ShoppingCart, Truck, Clock, Leaf, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function HomePage() {
    const { isAuthenticated } = useAuth();

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img 
                        src="https://images.unsplash.com/photo-1598962099619-528a224cb625?crop=entropy&cs=srgb&fm=jpg&q=85"
                        alt="Fresh produce"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#1B4D3E]/90 to-[#1B4D3E]/70" />
                </div>
                
                <div className="container-main relative z-10 py-20 md:py-32">
                    <div className="max-w-2xl">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 animate-fadeIn">
                            Henry's Aussie<br />
                            <span className="text-green-400">Fruit & Veggies</span>
                        </h1>
                        <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed animate-fadeIn delay-100">
                            Fresh • Local • Quality. Your trusted wholesale fruit and vegetable supplier. 
                            Premium Australian produce delivered to your door.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn delay-200">
                            <Link to="/shop">
                                <Button 
                                    data-testid="shop-now-btn"
                                    className="btn-primary text-lg px-8 py-4 h-auto w-full sm:w-auto"
                                >
                                    <ShoppingCart className="w-5 h-5 mr-2" />
                                    Shop Now
                                </Button>
                            </Link>
                            {isAuthenticated ? (
                                <Link to="/dashboard">
                                    <Button 
                                        data-testid="dashboard-btn"
                                        variant="outline" 
                                        className="btn-ghost text-white border-white hover:bg-white/10 text-lg px-8 py-4 h-auto w-full sm:w-auto"
                                    >
                                        My Dashboard
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            ) : (
                                <Link to="/login">
                                    <Button 
                                        data-testid="login-btn"
                                        variant="outline" 
                                        className="btn-ghost text-white border-white hover:bg-white/10 text-lg px-8 py-4 h-auto w-full sm:w-auto"
                                    >
                                        Login / Reorder
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust Indicators */}
            <section className="py-12 bg-white border-y border-slate-100">
                <div className="container-main">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        <div className="flex flex-col items-center text-center" data-testid="trust-family">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                                <Users className="w-7 h-7 text-[#1B4D3E]" />
                            </div>
                            <h3 className="font-semibold text-slate-900">Quality First</h3>
                            <p className="text-sm text-slate-500">Premium produce</p>
                        </div>
                        <div className="flex flex-col items-center text-center" data-testid="trust-fresh">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                                <Leaf className="w-7 h-7 text-[#1B4D3E]" />
                            </div>
                            <h3 className="font-semibold text-slate-900">Farm Fresh</h3>
                            <p className="text-sm text-slate-500">Local & Organic</p>
                        </div>
                        <div className="flex flex-col items-center text-center" data-testid="trust-delivery">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                                <Truck className="w-7 h-7 text-[#1B4D3E]" />
                            </div>
                            <h3 className="font-semibold text-slate-900">Fast Delivery</h3>
                            <p className="text-sm text-slate-500">Local area</p>
                        </div>
                        <div className="flex flex-col items-center text-center" data-testid="trust-schedule">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                                <Clock className="w-7 h-7 text-[#1B4D3E]" />
                            </div>
                            <h3 className="font-semibold text-slate-900">Weekly Schedule</h3>
                            <p className="text-sm text-slate-500">Delivered Tue · Pickup Sat</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Preview */}
            <section className="py-16 md:py-24">
                <div className="container-main">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                        Shop by Category
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Link to="/shop?category=Vegetables" className="group" data-testid="category-vegetables">
                            <div className="relative h-64 rounded-2xl overflow-hidden shadow-soft">
                                <img 
                                    src="https://images.unsplash.com/flagged/photo-1579410137879-fb3835b4f2eb?crop=entropy&cs=srgb&fm=jpg&q=85"
                                    alt="Fresh Vegetables"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-6">
                                    <h3 className="text-2xl font-bold text-white mb-1">Vegetables</h3>
                                    <p className="text-white/80">Fresh from the farm</p>
                                </div>
                            </div>
                        </Link>
                        <Link to="/shop?category=Fruit" className="group" data-testid="category-fruit">
                            <div className="relative h-64 rounded-2xl overflow-hidden shadow-soft">
                                <img 
                                    src="https://images.pexels.com/photos/4916239/pexels-photo-4916239.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                    alt="Fresh Fruits"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-6">
                                    <h3 className="text-2xl font-bold text-white mb-1">Fruit</h3>
                                    <p className="text-white/80">Seasonal selections</p>
                                </div>
                            </div>
                        </Link>
                        <Link to="/shop?category=Herbs" className="group" data-testid="category-herbs">
                            <div className="relative h-64 rounded-2xl overflow-hidden shadow-soft">
                                <img 
                                    src="https://images.pexels.com/photos/7076754/pexels-photo-7076754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                    alt="Fresh Herbs"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute bottom-0 left-0 p-6">
                                    <h3 className="text-2xl font-bold text-white mb-1">Herbs & Specialty</h3>
                                    <p className="text-white/80">Premium quality</p>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-16 md:py-24 bg-white">
                <div className="container-main">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center">
                        How It Works
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-orange-500 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                                1
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Browse & Add</h3>
                            <p className="text-slate-600">Quick-add your favorites with one tap</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-orange-500 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                                2
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Checkout</h3>
                            <p className="text-slate-600">Order by Sat 12pm for Tuesday delivery, or Thu 12pm for Saturday pickup</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-orange-500 text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                                3
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Receive</h3>
                            <p className="text-slate-600">Delivered Tuesday to your door, or collect Saturday at the market</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* B2B CTA */}
            <section className="py-16 md:py-24 bg-[#1B4D3E]">
                <div className="container-main text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Wholesale Accounts Welcome
                    </h2>
                    <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                        Restaurants, retailers, and businesses — create an account for saved pricing, 
                        quick reorders, and dedicated support.
                    </p>
                    <Link to="/register">
                        <Button 
                            data-testid="create-account-btn"
                            className="btn-primary text-lg px-8 py-4 h-auto"
                        >
                            Create Business Account
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}
